import { useCallback, useRef, useEffect } from "react";

export interface UseAutoScrollOptions {
  scrollContainerRef: React.RefObject<HTMLElement | null>;
  axis?: "vertical" | "horizontal" | "both";
  zoneSize?: number;
  maxSpeed?: number;
  enabled?: boolean;
}

export interface UseAutoScrollReturn {
  updateCursorPosition: (clientX: number, clientY: number) => void;
  startAutoScroll: () => void;
  stopAutoScroll: () => void;
  isScrolling: boolean;
}

const DEFAULT_ZONE_SIZE = 150; // pixels from edge to trigger scroll
const DEFAULT_MAX_SPEED = 10; // pixels per frame

/**
 * Custom hook for auto-scrolling a container based on cursor position
 * Works with any mouse/pointer event (drag, move, pointer, etc.)
 *
 * The scroll speed is determined purely by distance from the edge:
 * - Closer to edge = faster scroll
 * - Uses requestAnimationFrame for smooth 60fps scrolling
 * - Only updates cursor position on mouse events (no accumulated speed)
 *
 * @param options Configuration options for auto-scroll behavior
 * @returns Object with methods to control auto-scrolling
 */
export const useAutoScroll = (
  options: UseAutoScrollOptions
): UseAutoScrollReturn => {
  const {
    scrollContainerRef,
    axis = "vertical",
    zoneSize = DEFAULT_ZONE_SIZE,
    maxSpeed = DEFAULT_MAX_SPEED,
    enabled = true,
  } = options;

  const cursorPositionRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const rafIdRef = useRef<number | null>(null);
  const isScrollingRef = useRef(false);

  /**
   * Calculate and apply scroll based on current cursor position
   */
  const scroll = useCallback(() => {
    const container = scrollContainerRef.current;

    if (!container || !enabled) return;

    const { x: clientX, y: clientY } = cursorPositionRef.current;

    // Detect if scrolling the entire page vs a specific container
    const isDocumentScroll = container === document.documentElement || container === document.body;

    let scrollX = 0;
    let scrollY = 0;

    // Calculate vertical scroll
    if (axis === "vertical" || axis === "both") {
      let distanceFromTop: number;
      let distanceFromBottom: number;

      if (isDocumentScroll) {
        // Page-level scrolling: use viewport dimensions
        distanceFromTop = clientY;
        distanceFromBottom = window.innerHeight - clientY;
      } else {
        // Container scrolling: use container bounding rect
        const rect = container.getBoundingClientRect();
        distanceFromTop = clientY - rect.top;
        distanceFromBottom = rect.bottom - clientY;
      }

      if (distanceFromTop < zoneSize && distanceFromTop > 0) {
        // Scroll up - intensity based on distance from top edge
        const intensity = 1 - distanceFromTop / zoneSize;
        scrollY = -maxSpeed * intensity;
      } else if (distanceFromBottom < zoneSize && distanceFromBottom > 0) {
        // Scroll down - intensity based on distance from bottom edge
        const intensity = 1 - distanceFromBottom / zoneSize;
        scrollY = maxSpeed * intensity;
      }
    }

    // Calculate horizontal scroll
    if (axis === "horizontal" || axis === "both") {
      let distanceFromLeft: number;
      let distanceFromRight: number;

      if (isDocumentScroll) {
        // Page-level scrolling: use viewport dimensions
        distanceFromLeft = clientX;
        distanceFromRight = window.innerWidth - clientX;
      } else {
        // Container scrolling: use container bounding rect
        const rect = container.getBoundingClientRect();
        distanceFromLeft = clientX - rect.left;
        distanceFromRight = rect.right - clientX;
      }

      if (distanceFromLeft < zoneSize && distanceFromLeft > 0) {
        // Scroll left - intensity based on distance from left edge
        const intensity = 1 - distanceFromLeft / zoneSize;
        scrollX = -maxSpeed * intensity;
      } else if (distanceFromRight < zoneSize && distanceFromRight > 0) {
        // Scroll right - intensity based on distance from right edge
        const intensity = 1 - distanceFromRight / zoneSize;
        scrollX = maxSpeed * intensity;
      }
    }

    // Apply scroll if needed
    if (scrollX !== 0 || scrollY !== 0) {
      container.scrollBy(scrollX, scrollY);
    }

    // Continue scrolling loop
    if (isScrollingRef.current) {
      rafIdRef.current = requestAnimationFrame(scroll);
    }
  }, [scrollContainerRef, axis, zoneSize, maxSpeed, enabled]);

  /**
   * Update cursor position (call this on mouse/drag events)
   */
  const updateCursorPosition = useCallback(
    (clientX: number, clientY: number) => {
      cursorPositionRef.current = { x: clientX, y: clientY };
    },
    []
  );

  /**
   * Start auto-scroll loop
   */
  const startAutoScroll = useCallback(() => {
    if (isScrollingRef.current || !enabled) return;

    isScrollingRef.current = true;
    rafIdRef.current = requestAnimationFrame(scroll);
  }, [scroll, enabled]);

  /**
   * Stop auto-scroll loop
   */
  const stopAutoScroll = useCallback(() => {
    isScrollingRef.current = false;
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAutoScroll();
    };
  }, [stopAutoScroll]);

  return {
    updateCursorPosition,
    startAutoScroll,
    stopAutoScroll,
    isScrolling: isScrollingRef.current,
  };
};
